import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { MagicLinkInfoPage } from './pages/MagicLinkInfoPage';
import { PatientHistoryPage } from './pages/PatientHistoryPage';
import { MagicLinkPage } from './pages/MagicLinkPage';
import { MeetingLinkPage } from './pages/MeetingLinkPage';
import { PatientMeetingRoomPage } from './pages/PatientMeetingRoomPage';
import { storage } from './utils/storage';
import { authService } from './services/api/authService';
import ParticleBackground from './components/ParticleBackground';
import { I18nProvider } from './i18n';
import './App.scss';

// Protected Route wrapper component
const ProtectedRoute = ({
  children,
  isLoggedIn,
}: {
  children: JSX.Element;
  isLoggedIn: boolean;
}) => {
  if (!isLoggedIn) {
    return <Navigate to="/patient/info" replace />;
  }
  return children;
};

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuthStatus = () => {
      const authData = storage.getAuthData();
      if (authData) {
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
        storage.clearAuthData();
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, []);

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      storage.clearAuthData();
      setIsLoggedIn(false);
    } catch (error) {
      console.error('Logout failed:', error);
      storage.clearAuthData();
      setIsLoggedIn(false);
    }
  };

  if (isLoading) {
    return <div></div>;
  }

  return (
    <I18nProvider>
    <BrowserRouter>
      <div data-component="App">
        <ParticleBackground />
        <Routes>
          {/* Root redirect */}
          <Route
            path="/"
            element={<Navigate to="/patient/info" replace />}
          />

          {/* Magic Link Consultation Route */}
          <Route
            path="/consultation"
            element={
              isLoggedIn ? (
                <Navigate to="/patient/dashboard" replace />
              ) : (
                <MagicLinkPage onLoginSuccess={handleLoginSuccess} />
              )
            }
          />

          {/* Meeting Link Route */}
          <Route
            path="/meeting/:meetingId"
            element={<MeetingLinkPage onLoginSuccess={handleLoginSuccess} />}
          />

          {/* Patient Meeting Room Route */}
          <Route
            path="/patient/meeting-room/:meetingId"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <PatientMeetingRoomPage />
              </ProtectedRoute>
            }
          />

          {/* Patient routes */}
          <Route
            path="/patient/info"
            element={
              isLoggedIn ? (
                <Navigate to="/patient/dashboard" replace />
              ) : (
                <MagicLinkInfoPage />
              )
            }
          />
          <Route
            path="/patient/login"
            element={<Navigate to="/patient/info" replace />}
          />
          <Route
            path="/patient/dashboard"
            element={
              <ProtectedRoute isLoggedIn={isLoggedIn}>
                <PatientHistoryPage onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          {/* Catch all redirect */}
          <Route
            path="*"
            element={<Navigate to="/patient/info" replace />}
          />
        </Routes>
      </div>
    </BrowserRouter>
    </I18nProvider>
  );
}

export default App;